import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useEscapeKey from 'components/common/UseEscapeKey/UseEscapeKey';
import SelectTargetMenuModal from 'components/SelectTargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TARGET_MENU_BAR_GUIDANCE } from 'utils';
import { barMenuListActions, selectBarMenuList } from '../barMenuSlice';
import ButtonCustom from '../components/ButtonCustom';
import Confirmation from '../components/ConfirmDialog';
import { actionType } from '../models/ActionType';
import { BarMenu, BarMenuSerializers } from '../models/IBarMenu';
import { BarMenuListProps } from '../models/IBarMenuListProps';
import { ButtonRef } from '../models/IButtonRef';
import './BarGuideMenuList.scss';

const BarGuideMenuList: React.FC<BarMenuListProps> = ({ barInfoId, barInfoName, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);
  const barMenuListSelector = useAppSelector(selectBarMenuList);

  // state
  const [barMenuList, setBarMenuList] = useState<Array<BarMenu>>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isOpenSelectTargetMenuModal, setIsOpenSelectTargetMenuModal] = useState(false);

  //type
  const action = useRef<actionType>();

  // button ref
  const buttonDeleteRef = useRef<ButtonRef>(null);
  const buttonAddNewRef = useRef<ButtonRef>(null);

  //ref
  const refCheckedBarIds = useRef<Array<number>>([]);
  const refCheckBoxes = useRef<Array<HTMLInputElement>>([]);
  const refSelectedMenuIds = useRef<Array<number>>([]);

  useLayoutEffect(() => {
    if (barInfoId > 0) {
      dispatch(barMenuListActions.fetchListOfBarMenu(barInfoId));
    }
    return () => {
      dispatch(barMenuListActions.fetchListOfBarMenuFailed());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barInfoId]);

  useEffect(() => {
    setBarMenuList(barMenuListSelector);
    if (refCheckedBarIds.current.length > 0 && barMenuListSelector.length > 0 && action.current === 'Delete') {
      const nonDeletedBarsByIds = getBarsInArrayIds(barMenuListSelector, refCheckedBarIds.current, false);
      disableEnableButton(nonDeletedBarsByIds);
      uncheckAllCheckBoxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barMenuListSelector, dispatch]);

  const getBarsInArrayIds = (barMenuList: BarMenu[], ids: number[], isDeleted: boolean) => {
    return barMenuList.filter((item) => ids.includes(item.id) && item.delete_flg === isDeleted);
  };

  const handleCheckedBarInfo = (event: ChangeEvent<HTMLInputElement>) => {
    const id = event.currentTarget.id;
    const isChecked = event.currentTarget.checked;
    let updatedIds = [...refCheckedBarIds.current];

    if (isChecked && !updatedIds.includes(parseInt(id))) {
      updatedIds.push(parseInt(id));
    } else if (!isChecked) {
      const index = updatedIds.indexOf(parseInt(id));
      if (index !== -1) {
        updatedIds = updatedIds.filter((_, idx) => idx !== index);
      }
    }
    const nonDeletedBarsByIds = getBarsInArrayIds(barMenuListSelector, updatedIds, false);
    disableEnableButton(nonDeletedBarsByIds);
    refCheckedBarIds.current = updatedIds;
  };

  const handleAction = () => {
    if (action.current === 'Delete' && refCheckedBarIds.current.length > 0) {
      const filteredBars = getBarsInArrayIds(barMenuListSelector, refCheckedBarIds.current, false);
      let arrayOfIds = filteredBars.map((item) => item.id).sort();
      if (arrayOfIds.length > 0) {
        const actionFunc = barMenuListActions.deleteBarMenu;
        dispatch(actionFunc(arrayOfIds));
      }
    }
  };

  const handleConfirmDelete = () => {
    if (refCheckedBarIds.current.length > 0) {
      action.current = 'Delete';
      setShowConfirmDialog(true);
    }
  };

  const disableEnableButton = (nonDeletedBarsByIds: BarMenu[]) => {
    if (nonDeletedBarsByIds.length > 0) {
      buttonDeleteRef.current?.enable();
    } else {
      buttonDeleteRef.current?.disable();
    }
  };

  const closeModal = () => {
    action.current = undefined;
    refCheckedBarIds.current = [];
    onClose();
  };

  const openAddNewMenuModal = () => {
    action.current = 'Add';
    refSelectedMenuIds.current = barMenuList.map((obj) => obj.menu);
    setIsOpenSelectTargetMenuModal(true);
  };

  const handleReflectSelectTargetMenuModal = useCallback((menuIds: number[]) => {
    if (barInfoId > 0 && menuIds.length > 0) {
      const barMenus = menuIds.map((menuId) => {
        return { bar_info_id: barInfoId, menu_id: menuId } as BarMenuSerializers;
      });
      if (barMenus.length > 0) {
        dispatch(barMenuListActions.addNewBarMenu(barMenus));
      }
      setIsOpenSelectTargetMenuModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseSelectTargetMenuModal = useCallback(() => {
    buttonAddNewRef.current?.blur();
    action.current = undefined;
    refSelectedMenuIds.current = [];
    setIsOpenSelectTargetMenuModal(false);
  }, []);

  const uncheckAllCheckBoxes = () => {
    refCheckBoxes.current.forEach((checkbox) => {
      if (checkbox) checkbox.checked = false;
    });
    refCheckBoxes.current = [];
    refCheckedBarIds.current = [];
  };

  useEscapeKey(onClose);

  return (
    <>
      {showConfirmDialog && (
        <Confirmation
          okLabel={t('BarMenuTranslationFeature.DialogConfirm.btn_save')}
          cancelLabel={t('BarMenuTranslationFeature.DialogConfirm.btn_cancel')}
          title={t('BarMenuTranslationFeature.DialogConfirm.title')}
          confirmationMessage={
            action.current === 'Delete' ? t('BarMenuTranslationFeature.DialogConfirm.message_delete') : ''
          }
          onHide={() => {
            setShowConfirmDialog(false);
          }}
          enableEscape={false}
          proceed={() => {
            if (action.current) {
              handleAction();
            }
            setShowConfirmDialog(false); // Close the dialog after action
          }}
        />
      )}
      {isOpenSelectTargetMenuModal &&
        createPortal(
          <SelectTargetMenuModal
            targetSourceType={TARGET_MENU_BAR_GUIDANCE}
            targetName={barInfoName}
            exceptMenuIds={refSelectedMenuIds.current}
            onReflect={handleReflectSelectTargetMenuModal}
            onClose={handleCloseSelectTargetMenuModal}
          />,
          document.body
        )}
      <div className="static-modal">
        <Modal
          dialogClassName="bar-guide-menu-list"
          animation={true}
          show={true}
          centered={true}
          backdrop={'static'}
          keyboard={false}
          onHide={closeModal}
        >
          <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <FeatureTitleBar title={t('BarMenuTranslationFeature.Text.title')}></FeatureTitleBar>
          </Modal.Header>
          <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <div className="justify-content-md-center">
              <div className="fw-bold mb-0">
                {t('BarMenuTranslationFeature.Text.bar_guidance')}
                {barInfoName}
              </div>

              <div className="text-end mb-2">
                <ButtonCustom
                  class={'btn btn--dark-green'}
                  text={t('BarMenuTranslationFeature.Button.add')}
                  ref={buttonAddNewRef}
                  proceed={openAddNewMenuModal}
                ></ButtonCustom>
                <ButtonCustom
                  class={'btn btn--red ms-3'}
                  proceed={handleConfirmDelete}
                  text={t('BarListTranslationFeature.Button.delete')}
                  ref={buttonDeleteRef}
                  disabled={true}
                ></ButtonCustom>
              </div>
              <div className="sticky-table" style={{ height: 360, maxHeight: 360, background: '#999' }}>
                <Table className="text-center" hover>
                  <thead style={{ position: 'sticky', top: '0', backgroundColor: '#36489a' }} className="text-white">
                    <tr>
                      <th style={{ width: '10%' }}>{t('BarMenuTranslationFeature.Table.select')}</th>
                      <th style={{ width: '30%' }}>{t('BarMenuTranslationFeature.Table.menu_code')}</th>
                      <th style={{ width: '60%' }}>{t('BarMenuTranslationFeature.Table.menu_name')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {barMenuList && barMenuList.length > 0 ? (
                      barMenuList.map((barMenu, index) => {
                        return (
                          <tr key={index} className="text-center">
                            <td className="text-center">
                              <Form.Check
                                aria-label="option"
                                id={barMenu.id.toString()}
                                className="custom-checkbox"
                                defaultChecked={false}
                                onChange={handleCheckedBarInfo}
                                ref={(element: HTMLInputElement) => {
                                  refCheckBoxes.current[barMenu.id] = element;
                                }}
                              />
                            </td>
                            <td className="align-middle">{barMenu.menu_code}</td>
                            <td className="align-middle">{barMenu.menu_name}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};
export default BarGuideMenuList;
